/* eslint-disable max-len */
import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationButton from '../../../../components/NavigationButton/NavigationButton';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectCourseCodeListItems,
  selectCourseCodeLoadingListItems,
} from '../../../../redux/slices/courseCodeSlice';
import { fetchCourseCodeListItems } from '../../../../redux/thunks/courseCodeThunks';
import { TooltipAnchor } from '../../../../services/app.service';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import { canUserAccessRoute } from '../../../../services/security.service';
import { CourseCodeListItemModel } from '../../../../types/CourseCodeListItemModel';
import { formatToDateString } from '../../../../utilities/dateUtilities';
import { isStateAdmin } from '../../../../utilities/userUtilities';
import MdeOtssPage from '../MdeOtssPage';
import './CourseCodeList.css';

const CourseCodeList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(selectCourseCodeLoadingListItems);
  const listItems: CourseCodeListItemModel[] = useAppSelector(
    selectCourseCodeListItems
  );
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    const promise: unknown = dispatch(fetchCourseCodeListItems());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const handleEdit = (courseCodeId: string): void => {
    navigate(appRoutePaths.CourseCodeInput(courseCodeId));
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(event.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchValue('');
  };

  const trimmedSearchValue = searchValue.trim();
  const hasSearchValue = trimmedSearchValue.length > 0;
  const isMinimumSearchNotMet = hasSearchValue && trimmedSearchValue.length < 3;

  const filteredListItems = useMemo(() => {
    if (!hasSearchValue || isMinimumSearchNotMet) {
      return listItems;
    }

    const searchTerm = trimmedSearchValue.toLowerCase();

    return listItems.filter((courseCodeItem) => {
      const courseCodeNumber = courseCodeItem.courseCode?.toLowerCase() || '';
      const courseTitle = courseCodeItem.courseTitle?.toLowerCase() || '';
      return (
        courseCodeNumber.includes(searchTerm) ||
        courseTitle.includes(searchTerm)
      );
    });
  }, [hasSearchValue, isMinimumSearchNotMet, listItems, trimmedSearchValue]);

  const showNoResultsMessage =
    hasSearchValue && !isMinimumSearchNotMet && filteredListItems.length === 0;

  return (
    <MdeOtssPage
      pageClass="data-input--course-codes--list"
      isLoading={isLoading}
      loadingDataId={'course-code-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid="page-heading-text">Course Codes</h4>
            <div className="controls-container">
              <div className="course-code-search-container">
                <div className="course-code-search-input-wrapper">
                  <input
                    id="course-code-search"
                    data-testid="course-code-search"
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Enter at least 3 characters/numbers"
                    aria-label="Search by course code or course title"
                  />
                  {hasSearchValue && (
                    <button
                      type="button"
                      className="clear-search-button"
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                      data-testid="clear-search-button"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
              {isStateAdmin() && (
                <NavigationButton
                  route={appRoutePaths.CourseCodeInput('new')}
                  visibleOverride={canUserAccessRoute(
                    appRoutePaths.CourseCodeInput()
                  )}
                  classes="button no-wrap-text new-button"
                  tooltipText="Add New Course Code"
                >
                  <>Add New</>
                </NavigationButton>
              )}
            </div>
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Course Code</th>
                  <th>Course Title</th>
                  <th>Department</th>
                  <th>Inactive Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showNoResultsMessage ? (
                  <tr className="no-results-row">
                    <td colSpan={5}>No results match your search criteria</td>
                  </tr>
                ) : (
                  filteredListItems?.length > 0 &&
                  filteredListItems.map((courseCode) => (
                    <tr
                      key={`${courseCode.courseCode}`}
                      data-testid={courseCode.courseCode}
                    >
                      <td>{courseCode.courseCode}</td>
                      <td>{courseCode.courseTitle}</td>
                      <td>{courseCode.courseDepartmentName}</td>
                      <td>{formatToDateString(courseCode.courseEndDate)}</td>
                      <td>
                        <div className="action--column">
                          <button
                            data-testid={`edit-button-${courseCode.courseCode}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(courseCode.courseCode)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={'View/Edit Course Code'}
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MdeOtssPage>
  );
};

export default CourseCodeList;
